import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { CalendarClock, FileCode2, GitBranch, History, LayoutGrid, List, ListChecks, OctagonX, Pause, Pencil, Play, Plus, Upload } from 'lucide-react';
import { type Agent as AgentSchema, AGENT_RUNTIMES, AUTONOMY_LEVELS, MODEL_ALIASES, agentProfile, createAgent, listAgents, listDefinitions, pauseAgent, pauseAllAgents, resumeAgent, terminateAgent, updateAgent } from '@mkcore/agents/schema';
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  Textarea,
  toast,
} from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { ConfirmButton } from '@/components/confirm';
import { BudgetCard } from '@/features/agents/ai';
import { AgentLive, CockpitTotals, OrgChart, cockpitQuery } from '@/features/agents/cockpit';
import { useAgentsView } from '@/features/agents/view-pref';
import { BotAccountDialog } from '@/features/agents/workers';
import { ImportDialog, KnowledgeSpacePicker, RevisionsDialog, useManagerOptions } from '@/features/agents/governance';

export const Route = createFileRoute('/_app/agents/')({ component: Agents });

type Agent = z.output<typeof AgentSchema>;
const tone = { active: 'success', paused: 'warning', terminated: 'neutral' } as const;

/**
 * The agents of the company (CO-1233): identity, role, runtime and instructions, with pause, resume and terminate,
 * and the emergency stop for all of them at once (CO-1234); above them the AI budget of the month (CO-0972).
 */
function Agents() {
  const { t } = useTranslation();
  const [profileOf, setProfileOf] = useState<Agent | null>(null);
  const [botOf, setBotOf] = useState<Agent | null>(null);
  const [revisionsOf, setRevisionsOf] = useState<Agent | null>(null);
  const [importing, setImporting] = useState(false);
  const message = useErrorMessage();
  const canCreate = useCan('agents:create');
  const canUpdate = useCan('agents:update');
  const canManage = useCan('agents:manage');
  const q = useQuery({ queryKey: ['agent', 'agent'], queryFn: () => call(listAgents, {}) });
  const cockpit = useQuery(cockpitQuery);
  const invalidate = [['agent', 'agent']];
  const pause = useActionMutation(pauseAgent, { invalidate });
  const resume = useActionMutation(resumeAgent, { invalidate });
  const terminate = useActionMutation(terminateAgent, { invalidate });
  const stopAll = useActionMutation(pauseAllAgents, { invalidate, success: (o) => t('agents.pausedAll', { count: o.paused }) });
  const [editing, setEditing] = useState<Agent | 'new' | null>(null);
  const active = q.data?.items.filter((a) => a.status === 'active').length ?? 0;
  const [view, setView] = useAgentsView();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [runtimeFilter, setRuntimeFilter] = useState('');
  const needle = search.trim().toLowerCase();
  const filtered = (q.data?.items ?? []).filter(
    (a) =>
      (!statusFilter || a.status === statusFilter) &&
      (!runtimeFilter || a.runtime === runtimeFilter) &&
      (!needle || [a.name, a.role, a.description, a.profile, a.definition, ...a.roleKeys].some((v) => v?.toLowerCase().includes(needle))),
  );
  const filtering = Boolean(needle || statusFilter || runtimeFilter);

  return (
    <>
      <PageHeader
        title={t('agents.title')}
        description={t('agents.description')}
        actions={
          <div className="flex gap-2">
            <Link to="/agents/tasks" className={buttonVariants({ variant: 'secondary' })}>
              <ListChecks /> {t('agents.claims.title')}
            </Link>
            <Link to="/agents/routines" className={buttonVariants({ variant: 'secondary' })}>
              <CalendarClock /> {t('agents.routines.title')}
            </Link>
            {canUpdate && active > 0 && (
              <ConfirmButton
                variant="danger"
                icon={<OctagonX />}
                label={t('agents.stopAll')}
                title={t('agents.stopAllConfirm', { count: active })}
                description={t('agents.stopAllHint')}
                confirmLabel={t('agents.stopAll')}
                onConfirm={() => stopAll.mutateAsync({ input: { reason: t('agents.stopAllReason') } })}
              />
            )}
            {canCreate && (
              <Button variant="secondary" onClick={() => setImporting(true)}>
                <Upload /> {t('agents.governance.import')}
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setEditing('new')}>
                <Plus /> {t('agents.new')}
              </Button>
            )}
          </div>
        }
      />
      {cockpit.data && (
        <div className="mb-4">
          <CockpitTotals cockpit={cockpit.data} />
        </div>
      )}
      <div className="mb-4">
        <BudgetCard />
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-48" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('agents.none')} description={t('agents.noneHint')} />
      ) : (
        <>
        <div className="mb-3 flex flex-wrap gap-2">
          <Input type="search" aria-label={t('agents.search')} placeholder={t('agents.search')} className="max-w-sm" value={search} onChange={(e) => setSearch(e.target.value)} />
          <NativeSelect aria-label={t('agents.allStatuses')} className="w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('agents.allStatuses')}</option>
            {(['active', 'paused', 'terminated'] as const).map((st) => (
              <option key={st} value={st}>
                {t(`agents.status.${st}`)}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect aria-label={t('agents.allRuntimes')} className="w-auto" value={runtimeFilter} onChange={(e) => setRuntimeFilter(e.target.value)}>
            <option value="">{t('agents.allRuntimes')}</option>
            {AGENT_RUNTIMES.map((r) => (
              <option key={r} value={r}>
                {t(`agents.runtimes.${r}`)}
              </option>
            ))}
          </NativeSelect>
          <div role="group" aria-label={t('agents.view.label')} className="ml-auto flex gap-1">
            <Button size="sm" variant={view === 'cards' ? 'primary' : 'secondary'} aria-pressed={view === 'cards'} onClick={() => setView('cards')}>
              <LayoutGrid /> {t('agents.view.cards')}
            </Button>
            <Button size="sm" variant={view === 'list' ? 'primary' : 'secondary'} aria-pressed={view === 'list'} onClick={() => setView('list')}>
              <List /> {t('agents.view.list')}
            </Button>
          </div>
        </div>
        {!filtered.length ? (
          <EmptyState
            title={t('agents.noMatch')}
            action={
              filtering ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('');
                    setRuntimeFilter('');
                  }}
                >
                  {t('agents.clearFilters')}
                </Button>
              ) : undefined
            }
          />
        ) : (
        view === 'list' ? (
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-fg-3">
                <tr>
                  <th className="p-2">{t('common.name')}</th>
                  <th className="p-2">{t('agents.role')}</th>
                  <th className="p-2">{t('agents.runtime')}</th>
                  <th className="p-2">{t('agents.governance.manager')}</th>
                  <th className="p-2">{t('agents.view.status')}</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} aria-label={a.name} className="border-t border-line">
                    <td className="p-2">
                      <Link to="/agents/$id" params={{ id: a.id }} className="font-bold hover:underline">
                        {a.name}
                      </Link>
                    </td>
                    <td className="p-2 text-fg-2">{a.role}</td>
                    <td className="p-2 text-xs text-fg-3">{[t(`agents.runtimes.${a.runtime}`), a.modelAlias].filter(Boolean).join(' · ')}</td>
                    <td className="p-2 text-xs text-fg-3">{a.managerName ?? '—'}</td>
                    <td className="p-2">
                      <Badge tone={tone[a.status]}>{t(`agents.status.${a.status}`)}</Badge>
                    </td>
                    <td className="whitespace-nowrap p-2">
                      <AgentActions
                        a={a}
                        compact
                        canUpdate={canUpdate}
                        canManage={canManage}
                        pausing={pause.isPending ? pause.variables?.input.id : undefined}
                        resuming={resume.isPending ? resume.variables?.input.id : undefined}
                        onPause={(a) => pause.mutate({ input: { id: a.id, reason: '' } })}
                        onResume={(a) => resume.mutate({ input: { id: a.id } })}
                        onTerminate={(a) => terminate.mutateAsync({ input: { id: a.id, reason: '' } })}
                        onEdit={setEditing}
                        onRevisions={setRevisionsOf}
                        onProfile={setProfileOf}
                        onBot={setBotOf}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((a) => (
            <Card key={a.id} className="grid gap-2 p-4" aria-label={a.name}>
              <div className="flex items-start gap-2">
                <div className="grid flex-1 gap-0.5">
                  <Link to="/agents/$id" params={{ id: a.id }} className="font-bold hover:underline">
                    {a.name}
                  </Link>
                  <span className="text-sm text-fg-2">{a.role}</span>
                </div>
                <Badge tone={tone[a.status]}>{t(`agents.status.${a.status}`)}</Badge>
              </div>
              {a.description && <p className="text-sm text-fg-2">{a.description}</p>}
              {(() => {
                const live = cockpit.data?.agents.find((x) => x.id === a.id);
                return live ? <AgentLive a={live} currency={cockpit.data!.totals.currency} /> : null;
              })()}
              <p className="text-xs text-fg-3">
                {[a.runtime === 'hermes' ? `Hermes${a.profile ? ` · ${a.profile}` : ''}` : `${t(`agents.runtimes.${a.runtime}`)}${a.definition ? ` · ${a.definition}` : ''}`, a.modelAlias && t('agents.model', { alias: a.modelAlias }), a.defaultFor.length > 0 && t('agents.defaultFor', { domains: a.defaultFor.join(', ') }), a.roleKeys.join(', ')]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {(a.managerName || a.autonomy === 'propose' || a.knowledgeSpaces.length > 0) && (
                <p className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
                  {a.autonomy === 'propose' && <Badge tone="outline">{t('agents.governance.autonomy.propose')}</Badge>}
                  {a.managerName && <span>{t('agents.governance.reportsTo', { name: a.managerName })}</span>}
                  {a.knowledgeSpaces.length > 0 && <span>{t('agents.governance.knowledgeCount', { count: a.knowledgeSpaces.length })}</span>}
                </p>
              )}
              {a.status !== 'active' && a.statusAt && (
                <p className="text-xs text-fg-3">{t('agents.statusSince', { status: t(`agents.status.${a.status}`), at: formatDateTime(a.statusAt), by: a.statusByName ?? '—', reason: a.statusReason ?? '' })}</p>
              )}
              <AgentActions
                a={a}
                compact={false}
                canUpdate={canUpdate}
                canManage={canManage}
                pausing={pause.isPending ? pause.variables?.input.id : undefined}
                resuming={resume.isPending ? resume.variables?.input.id : undefined}
                onPause={(a) => pause.mutate({ input: { id: a.id, reason: '' } })}
                onResume={(a) => resume.mutate({ input: { id: a.id } })}
                onTerminate={(a) => terminate.mutateAsync({ input: { id: a.id, reason: '' } })}
                onEdit={setEditing}
                onRevisions={setRevisionsOf}
                onProfile={setProfileOf}
                onBot={setBotOf}
              />
            </Card>
          ))}
        </div>
        ))}
        </>
      )}
      {cockpit.data && (
        <div className="mt-4">
          <OrgChart cockpit={cockpit.data} />
        </div>
      )}
      {editing && <AgentDialog agent={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {profileOf && <ProfileDialog agent={profileOf} onClose={() => setProfileOf(null)} />}
      {botOf && <BotAccountDialog agent={botOf} onClose={() => setBotOf(null)} />}
      {revisionsOf && <RevisionsDialog agent={revisionsOf} onClose={() => setRevisionsOf(null)} />}
      {importing && <ImportDialog onClose={() => setImporting(false)} />}
    </>
  );
}

/** The buttons of an agent, shared by the card (with text) and the list row (icons with a label). */
function AgentActions({
  a,
  compact,
  canUpdate,
  canManage,
  pausing,
  resuming,
  onPause,
  onResume,
  onTerminate,
  onEdit,
  onRevisions,
  onProfile,
  onBot,
}: {
  a: Agent;
  compact: boolean;
  canUpdate: boolean;
  canManage: boolean;
  pausing: string | undefined;
  resuming: string | undefined;
  onPause: (a: Agent) => void;
  onResume: (a: Agent) => void;
  onTerminate: (a: Agent) => Promise<unknown>;
  onEdit: (a: Agent) => void;
  onRevisions: (a: Agent) => void;
  onProfile: (a: Agent) => void;
  onBot: (a: Agent) => void;
}) {
  const { t } = useTranslation();
  if (a.status === 'terminated' || !(canUpdate || canManage)) return null;
  const btn = (label: string, icon: React.ReactNode, onClick: () => void, variant: 'secondary' | 'ghost', loading?: boolean) => (
    <Button size="sm" variant={variant} loading={loading} aria-label={compact ? label : undefined} title={compact ? label : undefined} onClick={onClick}>
      {icon} {!compact && label}
    </Button>
  );
  return (
    <div className={compact ? 'flex flex-nowrap gap-2' : 'flex flex-wrap gap-2'}>
      {canUpdate && btn(t('common.edit'), <Pencil />, () => onEdit(a), 'secondary')}
      {canUpdate && a.status === 'active' && btn(t('agents.pause'), <Pause />, () => onPause(a), 'secondary', pausing === a.id)}
      {canUpdate && a.status === 'paused' && btn(t('agents.resume'), <Play />, () => onResume(a), 'secondary', resuming === a.id)}
      {btn(t('agents.governance.revisions', { number: a.revision }), <History />, () => onRevisions(a), 'ghost')}
      {canUpdate && a.runtime === 'hermes' && btn(t('agents.hermesProfile.open'), <FileCode2 />, () => onProfile(a), 'ghost')}
      {canManage && btn(t('agents.bot.open'), <GitBranch />, () => onBot(a), 'ghost')}
      {canManage && (
        <ConfirmButton
          variant="ghost"
          label={compact ? <span className="sr-only">{t('agents.terminate')}</span> : t('agents.terminate')}
          icon={compact ? <OctagonX /> : undefined}
          title={t('agents.terminateConfirm', { name: a.name })}
          description={t('agents.terminateHint')}
          confirmLabel={t('agents.terminate')}
          onConfirm={() => onTerminate(a)}
        />
      )}
    </div>
  );
}

function AgentDialog({ agent, onClose }: { agent: Agent | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [role, setRole] = useState(agent?.role ?? '');
  const [description, setDescription] = useState(agent?.description ?? '');
  const [profile, setProfile] = useState(agent?.profile ?? '');
  const [alias, setAlias] = useState<string>(agent?.modelAlias ?? '');
  const [defaultFor, setDefaultFor] = useState(agent?.defaultFor.join(', ') ?? '');
  const [instructions, setInstructions] = useState(agent?.instructions ?? '');
  const [approvalTools, setApprovalTools] = useState(agent?.approvalTools.join(', ') ?? '');
  const [autoApproveTools, setAutoApproveTools] = useState(agent?.autoApproveTools.join(', ') ?? '');
  const [mcpTools, setMcpTools] = useState(agent?.mcpTools.join(', ') ?? '');
  const [runtime, setRuntime] = useState<(typeof AGENT_RUNTIMES)[number]>(agent?.runtime ?? 'hermes');
  const [definition, setDefinition] = useState(agent?.definition ?? '');
  const definitions = useQuery({ queryKey: ['agent', 'definitions'], queryFn: () => call(listDefinitions, {}), enabled: runtime === 'in_app' });
  const [autonomy, setAutonomy] = useState<(typeof AUTONOMY_LEVELS)[number]>(agent?.autonomy ?? 'act');
  const [maxActions, setMaxActions] = useState(agent?.maxActionsBeforeApproval ? String(agent.maxActionsBeforeApproval) : '');
  const [managerId, setManagerId] = useState(agent?.managerId ?? '');
  const [knowledgeSpaces, setKnowledgeSpaces] = useState<string[]>(agent?.knowledgeSpaces ?? []);
  const [instructionsPath, setInstructionsPath] = useState(agent?.instructionsPath ?? '');
  const [workerLabel, setWorkerLabel] = useState(agent?.workerLabel ?? '');
  const [maxRunMinutes, setMaxRunMinutes] = useState(agent?.maxRunMinutes ? String(agent.maxRunMinutes) : '');
  const onWorker = runtime === 'claude_code' || runtime === 'cli';
  const managers = useManagerOptions(agent?.principalId ?? null);
  const invalidate = [['agent', 'agent']];
  const create = useActionMutation(createAgent, { invalidate, success: t('agents.created'), onSuccess: onClose });
  const update = useActionMutation(updateAgent, { invalidate, success: t('agents.saved'), onSuccess: onClose });
  const fields = {
    runtime,
    definition: runtime === 'in_app' ? definition || null : null,
    role: role.trim(),
    description,
    profile: profile.trim() || null,
    modelAlias: (alias || null) as (typeof MODEL_ALIASES)[number] | null,
    instructions,
    defaultFor: defaultFor
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    approvalTools: approvalTools
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    autoApproveTools: autoApproveTools
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    mcpTools: runtime === 'in_app'
      ? mcpTools
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    autonomy,
    maxActionsBeforeApproval: Number(maxActions) > 0 ? Math.round(Number(maxActions)) : null,
    managerId: managerId || null,
    knowledgeSpaces,
    instructionsPath: instructionsPath.trim() || null,
    workerLabel: onWorker ? workerLabel.trim() || null : null,
    maxRunMinutes: Number(maxRunMinutes) > 0 ? Math.round(Number(maxRunMinutes)) : null,
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{agent ? t('agents.edit', { name: agent.name }) : t('agents.new')}</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (agent) update.mutate({ input: { id: agent.id, expectedVersion: agent.version, ...fields } });
            else create.mutate({ input: { name: name.trim(), ...fields } });
          }}
        >
          {!agent && <Field label={t('common.name')}>{(p) => <Input {...p} required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />}</Field>}
          <Field label={t('agents.role')} hint={t('agents.roleHint')}>
            {(p) => <Input {...p} required maxLength={120} value={role} onChange={(e) => setRole(e.target.value)} />}
          </Field>
          <Field label={t('agents.descriptionField')}>{(p) => <Textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label={t('agents.runtime')} hint={runtime === 'hermes' ? undefined : runtime === 'in_app' ? t('agents.runtimeInAppHint') : t('agents.runtimeHint')}>
              {(p) => (
                <NativeSelect {...p} value={runtime} onChange={(e) => setRuntime(e.target.value as typeof runtime)}>
                  {AGENT_RUNTIMES.map((r) => (
                    <option key={r} value={r}>
                      {t(`agents.runtimes.${r}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {runtime === 'in_app' ? (
              <Field label={t('agents.definition')}>
                {(p) => (
                  <NativeSelect {...p} required value={definition} onChange={(e) => setDefinition(e.target.value)}>
                    <option value="">{t('agents.definitionChoose')}</option>
                    {(definitions.data?.items ?? []).map((d) => (
                      <option key={d.name} value={d.name} title={d.description}>
                        {d.title}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            ) : (
              <Field label={t('agents.profile')}>{(p) => <Input {...p} value={profile} onChange={(e) => setProfile(e.target.value)} placeholder="mkc-time" />}</Field>
            )}
            <Field label={t('agents.modelAlias')}>
              {(p) => (
                <NativeSelect {...p} value={alias} onChange={(e) => setAlias(e.target.value)}>
                  <option value="">{t('agents.modelDefault')}</option>
                  {MODEL_ALIASES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('agents.defaultForField')} hint={t('agents.defaultForHint')}>
              {(p) => <Input {...p} value={defaultFor} onChange={(e) => setDefaultFor(e.target.value)} placeholder="time" />}
            </Field>
          </div>
          <Field label={t('agents.approvalTools')} hint={t('agents.approvalToolsHint')}>
            {(p) => <Input {...p} className="font-mono text-xs" value={approvalTools} onChange={(e) => setApprovalTools(e.target.value)} placeholder="crm.*, time.entry.create" />}
          </Field>
          <Field label={t('agents.autoApproveTools')} hint={t('agents.autoApproveToolsHint')}>
            {(p) => <Input {...p} className="font-mono text-xs" value={autoApproveTools} onChange={(e) => setAutoApproveTools(e.target.value)} placeholder="dev.issue.comment" />}
          </Field>
          {runtime === 'in_app' && (
            <Field label={t('agents.mcpTools')} hint={t('agents.mcpToolsHint')}>
              {(p) => <Input {...p} className="font-mono text-xs" value={mcpTools} onChange={(e) => setMcpTools(e.target.value)} placeholder="woodpecker/*, bc/list_items" />}
            </Field>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('agents.governance.autonomyField')} hint={t(`agents.governance.autonomyHint.${autonomy}`)}>
              {(p) => (
                <NativeSelect {...p} value={autonomy} onChange={(e) => setAutonomy(e.target.value as typeof autonomy)}>
                  {AUTONOMY_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {t(`agents.governance.autonomy.${l}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('agents.governance.maxActions')} hint={t('agents.governance.maxActionsHint')}>
              {(p) => <Input {...p} inputMode="numeric" value={maxActions} onChange={(e) => setMaxActions(e.target.value.replace(/\D/g, ''))} />}
            </Field>
            <Field label={t('agents.governance.manager')}>
              {(p) => (
                <NativeSelect {...p} value={managerId} onChange={(e) => setManagerId(e.target.value)}>
                  <option value="">{t('agents.governance.noManager')}</option>
                  {managers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.kind === 'agent' ? `${m.name} (${t('agents.governance.agent')})` : m.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          <KnowledgeSpacePicker value={knowledgeSpaces} onChange={setKnowledgeSpaces} />
          <Field label={t('agents.governance.instructionsPath')} hint={t('agents.governance.instructionsPathHint')}>
            {(p) => <Input {...p} className="font-mono text-xs" value={instructionsPath} onChange={(e) => setInstructionsPath(e.target.value)} placeholder="mkc/agents/triage.md" />}
          </Field>
          <Field label={t('agents.governance.maxRunMinutes')} hint={t('agents.governance.maxRunMinutesHint')}>
            {(p) => <Input {...p} inputMode="numeric" value={maxRunMinutes} onChange={(e) => setMaxRunMinutes(e.target.value.replace(/\D/g, ''))} placeholder="30" />}
          </Field>
          {onWorker && (
            <Field label={t('agents.governance.workerLabel')} hint={t('agents.governance.workerLabelHint')}>
              {(p) => (
                <Input
                  {...p}
                  className="font-mono text-xs"
                  maxLength={40}
                  pattern="[a-z0-9][a-z0-9\-]{0,39}"
                  value={workerLabel}
                  onChange={(e) => setWorkerLabel(e.target.value.toLowerCase())}
                  placeholder="reports"
                />
              )}
            </Field>
          )}
          <Field label={t('agents.instructions')} hint={t('agents.instructionsHint')}>
            {(p) => <Textarea {...p} rows={8} className="font-mono text-xs" value={instructions} onChange={(e) => setInstructions(e.target.value)} />}
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending} disabled={!role.trim() || (!agent && !name.trim()) || (runtime === 'in_app' && !definition)}>
              {agent ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The MCP block for the Hermes profile of an agent, with the tool subset of its token (CO-0829), to copy. */
function ProfileDialog({ agent, onClose }: { agent: Agent; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const q = useQuery({ queryKey: ['agent', 'agent', agent.id, 'profile'], queryFn: () => call(agentProfile, { id: agent.id }), retry: false });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('agents.hermesProfile.title', { name: agent.name })}</DialogTitle>
        </DialogHeader>
        {q.isError ? (
          <p className="text-sm text-danger">{message(q.error)}</p>
        ) : !q.data ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="grid gap-2">
            <p className="text-sm text-fg-2">{t('agents.hermesProfile.hint', { envVar: q.data.envVar })}</p>
            <pre className="max-h-80 overflow-auto rounded-md bg-mono p-3 font-mono text-xs">{q.data.config}</pre>
          </div>
        )}
        <DialogFooter>
          <Button variant="secondary" disabled={!q.data} onClick={() => q.data && void navigator.clipboard.writeText(q.data.config).then(() => toast.success(t('agents.hermesProfile.copied')))}>
            {t('agents.hermesProfile.copy')}
          </Button>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
